<script lang="ts">
	import { run, preventDefault } from 'svelte/legacy';

	import { page } from '$app/stores';
	import { format } from 'date-fns';
	import { routes } from '$lib/routes';
	import { user } from '$lib/stores/user';
	import de from 'date-fns/locale/de/index.js'; // need to be imported like this https://github.com/sveltejs/kit/issues/612 | do not import entire "/locale" cuz it blows bundle size
	import GuestList from './GuestList.svelte';
	import GuestListEdit from './GuestListEdit.svelte';
	import AddToCalendarButton from './AddToCalendarButton.svelte';
	import AttendingPrompt from './AttendingPrompt.svelte';
	import IconPencil from '$lib/icons/IconPencil.svelte';
	import IconCheckCircle from '$lib/icons/IconCheckCircle.svelte';
	import IconQuestionCircle from '$lib/icons/IconQuestionCircle.svelte';
	import IconCrossCircle from '$lib/icons/IconCrossCircle.svelte';
	import IconAlarm from '$lib/icons/IconAlarm.svelte';
	import IconCash from '$lib/icons/IconCash.svelte';
	import IconGeo from '$lib/icons/IconGeo.svelte';
	import IconPeople from '$lib/icons/IconPeople.svelte';
	import IconOpenNew from '$lib/icons/IconOpenNew.svelte';
	import IconBasket from '$lib/icons/IconBasket.svelte';
	import IconTrash from '$lib/icons/IconTrash.svelte';
	import IconPlus from '$lib/icons/IconPlus.svelte';
	import IconInfoCircle from '~icons/heroicons/information-circle';
	import IconPhoto from '~icons/heroicons/camera';
	// import IconMessage from '~icons/mdi/message-text-outline';
	import EventCreatedPrompt from './EventCreatedPrompt.svelte';
	import { isShareApiSupported, openGraphTags, resolveUrls, sleep } from '$lib/common';
	import { browser } from '$app/environment';
	import InputText from '$lib/InputText.svelte';
	import InnerInputButton from '$lib/InnerInputButton.svelte';
	import type { PageData } from './$types';
	import { invalidateAll } from '$app/navigation';
	import { onMount } from 'svelte';
	import { Attending } from '$lib/eventTypes';
	import { addBringItem, addMeAsVisitor, removeBringItem, unpinBroadcast } from './page.telefunc';
	import { isUserAttending, currentEvent } from '$lib/currentEvent';
	import { fly } from 'svelte/transition';
	import { flip } from 'svelte/animate';
	import LoadingButton from '$lib/LoadingButton.svelte';
	import { cubicInOut } from 'svelte/easing';
	import ShareButton from './ShareButton.svelte';
	import Cover from '$lib/components/Cover.svelte';
	import Avatar from '$lib/Avatar.svelte';
	import { displayPrice, isMaybeAnswerEnabled } from '$lib/commonLogic';
	import EventTableView from './edit/EventTableView.svelte';
	import MessageBroadcast from './edit/MessageBroadcast.svelte';
	import CopyToClipboardButton from '$lib/components/CopyToClipboardButton.svelte';
	import CloneEvent from './CloneEvent.svelte';
	import { fade } from 'svelte/transition';
	import IconFlower from '$lib/icons/IconFlower.svelte';
	import * as m from '$lib/paraglide/messages.js';
	import { getLocale } from '$lib/paraglide/runtime';

	interface Props {
		data: PageData;
	}

	let { data }: Props = $props();

	let showGuestList = $state(false);
	let showAttendPrompt = $state(false);
	let hideBringInputs = $state(true);
	let bringTextInput = $state('');
	let addItemButtonLoading = $state(false);
	let selectedTab = $state('info' as 'info' | 'photos' | 'activity');
	let newGuestAttending = $state(undefined as Attending | undefined);

	onMount(async () => {
		if (browser) {
			if (!data.event.visitors.some((x) => x.userId === $user.id) && !$page.url.searchParams.get('doNotAddUser')) {
				await sleep(2000); // if it's a first time user his userId is often not yet saved in DB (see user.ts), so we wait a bit
				await addMeAsVisitor(data.event.id);
			}
		}
	});

	function formatDate(dateStr?: string | null) {
		if (!dateStr) return m.event_invalidDate();
		const date = new Date(dateStr);
		return `${format(date, 'do MMMM y', { locale: getLocale() === 'de' ? de : undefined })}`;
	}
	function formatTime(dateStr?: string | null) {
		if (!dateStr) return m.event_invalidDate();
		const date = new Date(dateStr);
		return `${format(date, 'EEEE p', { locale: getLocale() === 'de' ? de : undefined })}`;
	}

	function resolveHrTags(text: string) {
		const selfMadeLineSeperators = /(^\s*)?^[—|\-|=|–|‒|⸻|⸺|―|⁓|~|#]{3,}$(\n*\s*\n)?/gm; // At least 3 chars long (e.g: --- ===) + whitespace lines before/after.
		return text.replace(selfMadeLineSeperators, '<hr class="border-violet-200 my-6">');
	}

	async function addItem() {
		addItemButtonLoading = true;
		try {
			await addBringItem(data.event.id, bringTextInput);
			await invalidateAll();
			bringTextInput = '';
		} finally {
			addItemButtonLoading = false;
		}
	}

	async function removeItem(item: { id: string; name: string }) {
		if (!confirm(m.event_deleteItemConfirm({ name: item.name }))) return;
		await removeBringItem(data.event.id, item.id);
		invalidateAll();
	}

	function guestName(userId: string) {
		let name = data.event.owners.find((x) => x.userId === userId)?.user?.name;
		if (name !== undefined) return name;
		return data.event.guests.find((x) => x.user?.id === userId)?.name;
	}

	async function onUnpinBroadcast(broadcastId: number) {
		if (!confirm(m.event_deleteBroadcastConfirm())) return;
		await unpinBroadcast(data.event.id, broadcastId);
		await invalidateAll();
	}
	run(() => {
		currentEvent.set(data.event);
	});
	let publicUrl = $derived($page.url.origin + routes.event(data.event.slug));
	let attending = $derived(isUserAttending($user.id, data.event));
	let isAttendingUnknown = $derived(attending === 'UNKNOWN');
	let userIsOwner = $derived(data.event.owners.some((x) => x.userId === $user.id));
	let userCanBringItems = $derived(userIsOwner || attending === 'MAYBE' || attending === 'ATTENDING');
	let guestsAttending = $derived(
		data.event.guests
			.filter((x) => x.attending === 'ATTENDING')
			.map((x) => x.companions.length + 1)
			.reduce((a, b) => a + b, 0)
	);
	let userIsGuest = $derived(
		data.event.guests.some((x) => x.user?.id === $user.id && (x.attending === 'ATTENDING' || x.attending === 'MAYBE'))
	);
	let maybeAnswerEnabled = $derived(isMaybeAnswerEnabled(data.event));
	let guestListViewable = $derived(
		userIsOwner ||
			data.event.guestVisibility === 'public' ||
			(data.event.guestVisibility === 'guests' && userIsGuest) ||
			(data.event.guestVisibility === 'guests_count_public' && userIsGuest)
	);
	let guestCountVisible = $derived(
		guestListViewable ||
			data.event.guestVisibility === 'guests_count_public' ||
			data.event.guestVisibility === 'owners_count_public'
	);
	let attendingMessage = $derived.by(() => {
		const guest = data.event.guests.find((x) => x.user?.id === $user.id);
		const name = guest?.name ?? $user.name ?? '';

		if (!data.eventIsInThePast) {
			if (attending === Attending.WAITLIST) return m.youAreOnWaitlist({ name });
			if (attending === Attending.ATTENDING && data.event.isPublic) return m.youAreRegistered({ name });
			if (attending === Attending.NOT_ATTENDING && data.event.isPublic) return m.youAreUnregistered({ name });
			if (attending === Attending.ATTENDING) return m.youAccepted({ name });
			if (attending === Attending.MAYBE) return m.youMaybeAttending({ name });
			if (attending === Attending.NOT_ATTENDING) return m.youDeclined({ name });
		} else {
			if (attending === Attending.WAITLIST) return m.youWereOnWaitlist();
			if (attending === Attending.ATTENDING && data.event.isPublic) return m.youWereRegistered();
			if (attending === Attending.NOT_ATTENDING && data.event.isPublic) return m.youWereUnregistered();
			if (attending === Attending.ATTENDING) return m.youHadAccepted();
			if (attending === Attending.MAYBE) return m.youHadMaybeAttending();
			if (attending === Attending.NOT_ATTENDING) return m.youHadDeclined();
		}
		return '';
	});
</script>

<svelte:head>
	<title>{data.event?.name} ⳼ create.Party</title>

	{@html openGraphTags(data.event)}
</svelte:head>

<div class="sheet">
	<Cover
		imageUrl={data.event.coverImageUrl}
		effectConfig={data.event.coverEffect}
		eventName={data.event.name}
		eventStart={data.event.beginAt}
		roundedTop={true}
	/>

	<div class="relative z-10 flex items-center justify-center bg-gray-100 px-4 py-5">
		{#if userIsOwner}
			<div class="flex flex-col items-center justify-center pb-2">
				<p class="text-center">{m.sharePageToInviteGuests()}</p>

				<div class="mt-4 flex flex-wrap justify-center gap-4 sm:gap-6">
					<CopyToClipboardButton textToCopy={publicUrl} class={isShareApiSupported ? 'btn-secondary' : 'btn-primary'}>
						{m.copyLink()}
					</CopyToClipboardButton>

					<ShareButton url={publicUrl} class="min-w-fit grow sm:grow-0">{m.share()}</ShareButton>

					<a
						href={routes.editEvent(data.event.slug, data.event.ownerSecret ?? '')}
						data-sveltekit-preload-code="viewport"
						data-sveltekit-preload-data="hover"
						class="btn btn-secondary min-w-fit grow text-center sm:grow-0"
						data-testid="edit-event-btn"
					>
						<IconPencil class="mb-1 mr-1 inline-block" />
						{m.edit()}
					</a>

					<EventTableView event={data.event} class="min-w-fit grow px-2 py-1 sm:grow-0" />

					<MessageBroadcast class="min-w-fit grow px-2 py-1 sm:grow-0" />

					{#if data.event.isPublic}
						<CloneEvent />
					{/if}
				</div>
				<p class="text-gray-5000 mt-4 text-center">{m.openInIncognitoForGuestView()}</p>
			</div>
		{:else if data.eventIsInThePast}
			<div class="text-center">
				<p class="text-gray-600">{m.eventIsOver()}</p>
				<p class="text-gray-600">{attendingMessage}</p>
			</div>
		{:else if !isAttendingUnknown}
			<span class="px-4" data-testid="user-attending-container">{attendingMessage}</span>
			<button onclick={() => (showAttendPrompt = true)} class="btn btn-secondary" data-testid="change-attending-btn">
				<IconPencil class="mb-1 mr-1 inline" />
				<span class="hidden sm:inline">{m.change()}</span>
			</button>
		{:else if data.event.isPublic}
			<button
				onclick={() => {
					newGuestAttending = Attending.ATTENDING;
					showAttendPrompt = true;
				}}
				class="btn btn-primary mx-3"
				data-testid="attend-btn"
			>
				<IconCheckCircle class="mb-1 mr-1 inline" />
				{m.register()}
			</button>
		{:else}
			<button
				onclick={() => {
					newGuestAttending = Attending.ATTENDING;
					showAttendPrompt = true;
				}}
				class="btn btn-primary mx-3"
				data-testid="attend-btn"
			>
				<IconCheckCircle class="mb-1 mr-1 inline" />
				<div class="inline-block whitespace-nowrap">
					{m.accept()}
				</div>
			</button>
			{#if maybeAnswerEnabled}
				<button
					onclick={() => {
						newGuestAttending = Attending.MAYBE;
						showAttendPrompt = true;
					}}
					class="btn btn-secondary mx-3"
					data-testid="maybe-btn"
				>
					<IconQuestionCircle class="mb-1 mr-1 inline" />
					{m.maybe()}
				</button>
			{/if}
			<button
				onclick={() => {
					newGuestAttending = Attending.NOT_ATTENDING;
					showAttendPrompt = true;
				}}
				class="btn btn-secondary mx-3"
				data-testid="decline-btn"
			>
				<IconCrossCircle class="mb-1 mr-1 inline" />
				<div class="inline-block whitespace-nowrap">
					{m.decline()}
				</div>
			</button>
		{/if}
	</div>

	<div class="relative z-10 px-4 sm:px-12">
		<!-- Stats -->
		<div class=" mt-8 flex justify-center px-8 sm:px-0">
			<div class="flex flex-col flex-wrap justify-start gap-4 sm:flex-row sm:justify-center sm:gap-8">
				<div class=" flex p-1">
					<IconAlarm class="text-3xl" />
					<div class="ml-3">
						<div class="label" data-testid="event-begin-stat-date">
							{formatDate(data.event.beginAt)}
						</div>
						<div class="leading-none" data-testid="event-begin-stat-time">
							{formatTime(data.event.beginAt)}
						</div>
					</div>
					<div class=" ml-4 flex items-center justify-center">
						<AddToCalendarButton event={data.event} class="" data-testid="add-to-calendar-btn" />
					</div>
				</div>

				{#if data.event.price}
					<div class=" flex p-1">
						<IconCash class="text-3xl" />
						<div class="ml-3">
							<div class="label">{m.entryFee()}</div>
							<div class="leading-none" data-testid="event-price-container">{displayPrice(data.event)}</div>
						</div>
					</div>
				{/if}

				<div class=" flex p-1">
					<div class="flex items-center justify-center text-3xl">
						<IconGeo class="text-3xl" />
					</div>
					<div class="ml-3">
						<div class="label">{m.location()}</div>
						<div class="leading-none" data-testid="event-location-container">
							{data.event.location}
						</div>
					</div>
				</div>

				{#if guestCountVisible}
					<div class=" flex p-1">
						<div class="flex">
							<div class="flex items-center justify-center text-3xl">
								<IconPeople class="inline-block" />
							</div>
							<div class="ml-3">
								<div class="label">
									{data.event.isPublic ? m.participants() : m.guests()}
								</div>
								<div class="leading-none">
									<span data-testid="guests-attending-container">{guestsAttending}</span>

									{#if guestListViewable}
										<button
											class="ml-1 inline-block"
											onclick={() => (showGuestList = true)}
											data-testid="open-guest-list-btn"
										>
											<span class="link"> {m.show()} </span>
											<IconOpenNew class="mb-1 inline" />
										</button>
									{/if}
								</div>
							</div>
						</div>
					</div>
				{/if}
			</div>
		</div>

		<!-- Tabs -->
		{#if data.event.mediaUploadEnabled}
			<div class="mt-8 flex justify-center">
				<nav
					class="isolate flex max-w-lg flex-grow divide-x divide-gray-200 overflow-hidden rounded-lg border border-gray-200 shadow"
					aria-label="Tabs"
				>
					<button
						onclick={() => (selectedTab = 'info')}
						class="{selectedTab === 'info'
							? 'text-violet-700'
							: 'text-gray-500 hover:text-gray-700'} group relative flex min-w-0 flex-1 items-center justify-center overflow-hidden px-4 py-3 focus:z-10"
					>
						<IconInfoCircle class="size-[1.65rem]" />
						<span class="hidden pl-2 sm:inline"> {m.info()} </span>
						{#if selectedTab === 'info'}
							<span
								aria-hidden="true"
								class="absolute inset-x-0 bottom-0 h-0.5 bg-violet-500"
								transition:fade={{ duration: 150 }}
							></span>
						{/if}
					</button>
					<button
						onclick={() => (selectedTab = 'photos')}
						class="{selectedTab === 'photos'
							? 'text-violet-700'
							: 'text-gray-500 hover:text-gray-700'} group relative flex min-w-0 flex-1 items-center justify-center overflow-hidden px-4 py-3 focus:z-10"
					>
						<div class="relative">
							<IconPhoto class="size-[1.65rem]" />
							{#if data.event.images.length > 0}
								<span
									class="absolute -right-3 -top-2 inline-block rounded-full bg-violet-100 px-1 py-0.5 text-[0.6rem] font-medium text-violet-600"
								>
									{data.event.images.length}
								</span>
							{/if}
						</div>
						<span class="hidden pl-2 sm:inline"> {m.photos()} </span>
						{#if selectedTab === 'photos'}
							<span
								aria-hidden="true"
								class="absolute inset-x-0 bottom-0 h-0.5 bg-violet-500"
								transition:fade={{ duration: 150 }}
							></span>
						{/if}
					</button>
				</nav>
			</div>
		{/if}

		<!-- Photos -->
		{#if selectedTab === 'photos'}
			{#await import('./EventImageGallery.svelte')}
				<div class="mt-4 flex justify-center">
					<IconFlower class="size-8 animate-spin text-violet-500" />
				</div>
			{:then { default: EventImageGallery }}
				<div
					class="mt-4"
					in:fade={{ duration: 200, easing: cubicInOut }}
					out:fade={{ duration: 100, easing: cubicInOut }}
				>
					<EventImageGallery
						eventId={data.event.id}
						images={data.event.images}
						userIsAllowedToUpload={userIsOwner ||
							attending === 'ATTENDING' ||
							attending === 'MAYBE' ||
							$page.data.isUserAdmin}
						{userIsOwner}
					/>
				</div>
			{/await}
		{/if}

		{#if selectedTab === 'info'}
			<div in:fade={{ duration: 200, easing: cubicInOut }} out:fade={{ duration: 100, easing: cubicInOut }}>
				<!-- Description -->
				{#if data.event.description}
					<div class="mt-8 whitespace-pre-line break-words" data-testid="event-description-container">
						{#if data.event.description.length < 180}
							<div class="label">{m.description()}</div>
						{/if}
						{@html resolveHrTags(resolveUrls(data.event.description))}
					</div>
				{/if}

				<!-- Broadcasts -->
				{#if data.event.broadcasts.length > 0}
					<div class="mt-8">
						<div class="flex flex-col gap-4">
							{#each data.event.broadcasts as broadcast}
								<article class="rounded-md bg-violet-50 px-4 py-3">
									{#if userIsOwner}
										<LoadingButton onClick={() => onUnpinBroadcast(broadcast.id)} class="float-right rounded-full">
											<IconTrash class="size-4 text-violet-700" />
										</LoadingButton>
									{/if}
									<div class="label mb-1">
										{m.messageFrom({ date: format(new Date(broadcast.createdAt), 'dd.MM.yyyy HH:mm') })}
									</div>
									<div class="whitespace-pre-wrap">
										{broadcast.message}
									</div>
								</article>
							{/each}
						</div>
					</div>
				{/if}

				<!-- Owners -->
				<div class="mt-8">
					<div class="label mb-1">
						{data.event.owners.length > 1 ? m.organizers() : m.organizer()}
					</div>
					<div class="flex flex-wrap gap-x-6 gap-y-3">
						{#each data.event.owners.filter((x) => x.user?.name) as owner}
							<svelte:element
								this={owner.user?.authUserId ? 'a' : 'div'}
								href={routes.publicProfile(owner.userId)}
								class=" mb-1 flex w-fit items-center"
							>
								<Avatar
									user={{ userId: owner.userId, ...owner.user }}
									class="mr-2 translate-y-[0.1rem]"
									size="36px"
									innerSize="16px"
								/>
								<span class:link={owner.user?.authUserId}>
									{owner.user?.name}
								</span>
							</svelte:element>
						{/each}
					</div>
				</div>

				<!-- Mitbringen -->
				{#if data.event.bringItemsEnabled}
					<div class="mt-8">
						{#if hideBringInputs && data.event.items.length === 0}
							<div>
								{#if userCanBringItems}
									<button
										onclick={() => (hideBringInputs = !hideBringInputs)}
										class="btn btn-primary"
										data-testid="show-items-input-btn"
									>
										<IconBasket class="mb-1 mr-1 inline text-lg" />
										{m.iWillBringSomething()}
									</button>
								{/if}
							</div>
						{:else}
							<div>
								<h3 class="mb-2 text-xl font-semibold">
									<IconBasket class="mb-1 mr-3 inline-block text-2xl" />
									<div class="inline-block text-violet-800">{m.whatWillBeBrought()}</div>
								</h3>
								<ul class="pl-8" data-testid="items-container">
									{#each data.event.items as item (item.id)}
										<li transition:fly animate:flip={{ duration: 500, easing: cubicInOut }} class="list-disc">
											{item.name}
											{#if guestListViewable}
												<div class="inline-block pl-1 text-gray-600">
													{m.by({ name: guestName(item.userId) })}
													{data.event.guests.find((x) => x.user?.id === item.userId)?.attending === Attending.MAYBE
														? ` (${m.maybe()})`
														: ''}
												</div>
											{/if}
											{#if item.userId === $user.id}
												<button
													onclick={() => removeItem(item)}
													class="ml-1"
													title={m.delete_()}
													data-testid="item-delete-btn"
												>
													<IconTrash class="inline text-violet-700" />
												</button>
											{/if}
										</li>
									{/each}
								</ul>
								{#if userCanBringItems}
									<form onsubmit={preventDefault(addItem)} class="mt-4">
										<label for="bringText" class="label mb-2 block"> {m.iWillBring()} </label>
										<div class="flex">
											<InputText
												bind:value={bringTextInput}
												id="bringText"
												required
												class=""
												placeholder={m.potatoSalad()}
												autocomplete="off"
												data-testid="item-input"
											>
												{#snippet right()}
													<InnerInputButton loading={addItemButtonLoading} type="submit" data-testid="item-add-btn">
														<IconPlus class="inline text-2xl" />
														<span class="hidden sm:inline"> {m.add()} </span>
													</InnerInputButton>
												{/snippet}
											</InputText>
										</div>
									</form>
								{/if}
							</div>
						{/if}
					</div>
				{/if}
			</div>
		{/if}
	</div>
</div>

<AttendingPrompt
	bind:showModal={showAttendPrompt}
	{newGuestAttending}
	guestFromServer={data.guest}
	isMaybeAnswerEnabled={maybeAnswerEnabled}
/>

<EventCreatedPrompt />

<GuestList bind:open={showGuestList} />

<GuestListEdit />
